
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { UploadFile } from '@/shared/models/UploadFile';
import { required, email, sameAs } from '@vuelidate/validators';
import { estateAgentService } from '@/estate-agent/EstateAgentService';
import ResizableImage from '@/shared/components/ResizableImage.vue';
import useVuelidate from '@vuelidate/core';
import PhonePrefix from '@/shared/components/PhonePrefix.vue';
import config from '@/shared/constants/config';
import phoneNumber from '@/shared/helpers/phoneNumberValidator';

export default defineComponent({
  name: 'EstateAgentInsert',
  mixins: [Form],
  emits: ['inserted', 'close'],
  setup() {
    return { v$: useVuelidate() };
  },
  components: {
    ResizableImage,
    PhonePrefix,
  },
  data() {
    return {
      estateAgentImage: {} as null | UploadFile,
      selectedRole: '' as string,
      roles: [],
      form: {
        firstName: null,
        lastName: null,
        email: null,
        confirmEmail: null,
        phoneNumber: null,
        phoneNumberPrefix: config.DEFAULT_PHONE_PREFIX,
        isAdmin: false,
        languageId: (window as never)['languageId'],
        photo: null as null | string,
      },
    };
  },
  validations() {
    return {
      form: {
        firstName: { required },
        lastName: { required },
        email: { required, email },
        phoneNumberPrefix: { required },
        phoneNumber: { required, phoneNumber: phoneNumber(this.form.phoneNumberPrefix) },
        confirmEmail: { required, sameAsEmail: sameAs(this.form.email) },
      },
    };
  },
  async created() {
    this.isLoading = true;
    try {
      const result = await estateAgentService.getNew();
      this.roles = result.data.roles;
      this.selectedRole = (this.roles[0] as any).key;
    } catch (error) {
      console.error(error);
    }
  },
  methods: {
    async submit() {
      if (!this.validate()) {
        return;
      }

      this.startLoading();

      try {
        this.form.isAdmin = this.selectedRole === 'true';

        const form = Object.assign({}, this.form);

        if (form.phoneNumberPrefix && form.phoneNumberPrefix.value) {
          form.phoneNumberPrefix = form.phoneNumberPrefix.value;
        }

        await estateAgentService.insert(form);
        this.inserted();
        this.close();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.endLoading();
      }
    },
    async handleEstateAgentImageUpload() {
      const ref = this.$refs.estateAgentImage as any;
      const files = ref.files as string[] | Blob[];
      const file = files[0];
      this.estateAgentImage = {} as UploadFile;
      this.estateAgentImage.isUploading = true;
      this.estateAgentImage.uploadPercentage = 0;
      this.estateAgentImage.file = (file as any).name;
      const formData = new FormData();
      formData.append('file', file);

      try {
        const result = await estateAgentService.uploadPhoto(formData, (progressEvent: any) => {
          if (this.estateAgentImage) {
            this.estateAgentImage.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
          }
        });
        this.estateAgentImage.file = result.data.photo;
        this.estateAgentImage.url = result.data.url;
        this.estateAgentImage.isSuccess = true;
        this.form.photo = result.data.photo;
      } catch (error) {
        this.estateAgentImage.isError = true;
        this.estateAgentImage.text = 'Error';
        const errors = this.getApiErrors(error);
        this.estateAgentImage.text = errors[0].message;
      } finally {
        this.estateAgentImage.isUploading = false;
      }

      ref.value = null;
    },
    deleteEstateAgentFile() {
      this.estateAgentImage = {} as UploadFile;
      this.form.photo = null;
    },
    inserted() {
      this.$emit('inserted');
    },
    close() {
      this.$emit('close');
    },
  },
});
